// =============================================================================
// SelfServiceSignup.js
// -----------------------------------------------------------------------------
// Oeffentliche Buchung: Angebot waehlen, nur E-Mail und Passwort, Zustimmungen.
// Firmierung und Anschrift folgen nach der Anmeldung.
// =============================================================================

import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import UserService from "../services/user.service";
import FEATURES from "../config/features";
import { TRIAL_STORAGE_KEY } from "./HomeQrStudio";

// -----------------------------------------------------------------------------
// A3 – ALTCHA Proof-of-Work-Loeser (selbst gehostet, kein Drittanbieter).
// Loest die serverseitig erzeugte Challenge nach dem gleichen Verfahren wie der
// Server pruefen wird: kleinste Zahl n finden, deren SHA-256(salt + n) dem
// vorgegebenen challenge-Hash entspricht. Nutzt die Web Crypto API des Browsers.
// -----------------------------------------------------------------------------
const toHex = (buffer) =>
  Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

const sha256Hex = async (message) => {
  const data = new TextEncoder().encode(message);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  return toHex(digest);
};

const solveAltchaChallenge = async (challenge) => {
  const { algorithm, challenge: target, salt, maxnumber, signature } = challenge || {};
  const max = Number(maxnumber) || 1000000;
  for (let n = 0; n <= max; n += 1) {
    // eslint-disable-next-line no-await-in-loop
    const hash = await sha256Hex(`${salt}${n}`);
    if (hash === target) {
      const payload = { algorithm, challenge: target, number: n, salt, signature };
      return window.btoa(JSON.stringify(payload));
    }
  }
  throw new Error("ALTCHA_UNSOLVED");
};

const emptyForm = {
  custumer_company_email: "",
  password: "",
  password_repeat: "",
  privacy_consent: false,
  agb_consent: false,
  tariff_consent: false,
};

const MIN_PASSWORD_LENGTH = 10;

const PERIOD_LABELS = {
  onetime: "einmalig",
  daily: "pro Tag",
  monthly: "pro Monat",
  yearly: "pro Jahr",
};

const formatAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "–";
  return amount.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const SelfServiceSignup = () => {
  const navigate = useNavigate();

  const [catalog, setCatalog] = useState({ enabled: false, reason: null, host: null, offers: [] });
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("yearly");
  const [trial, setTrial] = useState(null);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [securityChecking, setSecurityChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [resendEmail, setResendEmail] = useState("");
  const [resent, setResent] = useState("");

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const response = await UserService.getSelfServiceCatalog();
      const data = response.data || { enabled: false, offers: [] };
      setCatalog(data);
      let stored = null;
      try {
        stored = JSON.parse(window.sessionStorage.getItem(TRIAL_STORAGE_KEY) || "null");
      } catch (error) {
        stored = null;
      }
      if (stored && stored.trial_token && stored.batch_id) {
        setTrial(stored);
        setSelectedBatchId(stored.batch_id);
        if (stored.billing_period) {
          setBillingPeriod(stored.billing_period);
        }
      } else if (data.offers && data.offers.length === 1) {
        setSelectedBatchId(data.offers[0].batch_id);
      }
    } catch (error) {
      setCatalog({ enabled: false, reason: "error", host: null, offers: [] });
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((previous) => ({ ...previous, [name]: type === "checkbox" ? checked : value }));
    setErrors((previous) => ({ ...previous, [name]: undefined }));
  };

  const validate = () => {
    const found = {};
    if (!selectedBatchId) found.batch_id = "Bitte ein Angebot auswählen.";
    if (!form.custumer_company_email.trim()) {
      found.custumer_company_email = "Bitte E-Mail-Adresse angeben.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.custumer_company_email.trim())) {
      found.custumer_company_email = "Bitte eine gültige E-Mail-Adresse angeben.";
    }
    if (form.password.length < MIN_PASSWORD_LENGTH) {
      found.password = `Das Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`;
    } else if (!/[A-Za-z]/.test(form.password) || !/[0-9]/.test(form.password)) {
      found.password = "Das Passwort muss Buchstaben und Ziffern enthalten.";
    }
    if (form.password !== form.password_repeat) {
      found.password_repeat = "Die Passwörter stimmen nicht überein.";
    }
    if (!form.privacy_consent) found.privacy_consent = "Bitte der Datenschutzerklärung zustimmen.";
    if (!form.agb_consent) found.agb_consent = "Bitte die Allgemeinen Geschäftsbedingungen akzeptieren.";
    if (!form.tariff_consent) found.tariff_consent = "Bitte die Preis- und Tarifkonditionen akzeptieren.";
    return found;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setApiError("");
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSubmitting(true);
    try {
      // A3 – ALTCHA-Bot-Schutz: nur aktiv, wenn Feature eingeschaltet ist.
      // Die Challenge wird frisch geholt und im Browser geloest; nur das
      // Ergebnis (Payload) wird mitgesendet. Faellt das Feature serverseitig
      // weg (enabled=false), wird ohne Pruefung fortgefahren (additiv).
      let altchaPayload;
      if (FEATURES.altcha) {
        setSecurityChecking(true);
        try {
          const challengeResponse = await UserService.getSelfServiceAltchaChallenge();
          const data = challengeResponse.data || {};
          if (data.enabled && data.challenge) {
            altchaPayload = await solveAltchaChallenge(data.challenge);
          }
        } catch (secError) {
          setSecurityChecking(false);
          setSubmitting(false);
          setApiError(
            "Die automatische Sicherheitspruefung ist fehlgeschlagen. Bitte laden Sie die Seite neu und versuchen Sie es erneut."
          );
          return;
        }
        setSecurityChecking(false);
      }

      const response = await UserService.registerSelfService({
        custumer_company_email: form.custumer_company_email,
        password: form.password,
        privacy_consent: form.privacy_consent,
        agb_consent: form.agb_consent,
        tariff_consent: form.tariff_consent,
        batch_id: selectedBatchId,
        billing_period: billingPeriod,
        trial_token: trial && String(trial.batch_id) === String(selectedBatchId) ? trial.trial_token : undefined,
        altcha: altchaPayload,
      });
      window.sessionStorage.removeItem(TRIAL_STORAGE_KEY);
      setResendEmail(form.custumer_company_email);
      setResult(response.data);
      setForm(emptyForm);
    } catch (error) {
      const data = error && error.response ? error.response.data : null;
      if (data && data.errors) setErrors(data.errors);
      setApiError(
        (data && data.message) ||
          "Die Registrierung konnte nicht abgeschlossen werden. Bitte später erneut versuchen."
      );
      loadCatalog();
    } finally {
      setSecurityChecking(false);
      setSubmitting(false);
    }
  };

  const resend = async () => {
    setResent("");
    try {
      const response = await UserService.resendSelfServiceConfirmation(resendEmail);
      setResent((response.data && response.data.message) || "Bitte prüfen Sie Ihr Postfach.");
    } catch (error) {
      setResent("Der Link konnte nicht erneut angefordert werden.");
    }
  };

  if (result) {
    return (
      <div className="container myn-signup">
        <h1 className="myn-signup__title">Bitte E-Mail bestätigen</h1>
        <Alert variant="success">
          <p className="mb-2">{result.message}</p>
          <p className="mb-0">
            Rechnungsnummer <strong>{result.invoice_number}</strong> über{" "}
            <strong>
              {formatAmount(result.amounts && result.amounts.gross_total)}{" "}
              {result.amounts && result.amounts.currency}
            </strong>{" "}
            brutto. Die Rechnung finden Sie nach der Anmeldung unter „Meine Rechnungen“.
          </p>
        </Alert>
        <p>
          Der QR-Code liegt in Ihrem Konto unter „Meine QR-Codes“. Nach der Bestätigung
          können Sie Firmierung und Anschrift ergänzen und den Code teilen oder herunterladen.
        </p>
        <Button onClick={resend} variant="outline-primary" className="me-2">
          Bestätigungslink erneut senden
        </Button>
        <Button onClick={() => navigate("/login")}>Zur Anmeldung</Button>
        {resent && <p className="mt-3">{resent}</p>}
      </div>
    );
  }

  if (catalogLoading) {
    return (
      <div className="container myn-signup">
        <Spinner animation="border" role="status" />
        <span className="ms-2">Angebote werden geladen …</span>
      </div>
    );
  }

  if (!catalog.enabled) {
    return (
      <div className="container myn-signup">
        <h1 className="myn-signup__title">Selbst buchen</h1>
        <Alert variant="info">
          Derzeit steht kein Angebot zur Selbstbuchung bereit. Bitte versuchen Sie es später erneut
          oder <Link to="/login">melden Sie sich an</Link>, wenn Sie bereits ein Konto haben.
        </Alert>
      </div>
    );
  }

  const selectedOffer = (catalog.offers || []).find((offer) => String(offer.batch_id) === String(selectedBatchId));
  const rawMonthly = selectedOffer ? Number(selectedOffer.tariff.amount_eur_net) || 0 : 0;
  const discountPercent = selectedOffer
    ? (Number(selectedOffer.tariff.yearly_discount_percent) || (selectedOffer.tariff.amount_period_net === "monthly" ? 20 : 0))
    : 0;
  const activationFee = selectedOffer ? Number(selectedOffer.tariff.activation_fee_eur_net) || 0 : 0;

  let yearlyNet = rawMonthly * 12;
  let yearlySavings = 0;
  let monthlyEquiv = rawMonthly;

  if (selectedOffer) {
    if (selectedOffer.tariff.amount_period_net === "monthly") {
      const fullYear = rawMonthly * 12;
      yearlySavings = fullYear * (discountPercent / 100);
      yearlyNet = fullYear - yearlySavings;
      monthlyEquiv = yearlyNet / 12;
    } else if (selectedOffer.tariff.amount_period_net === "yearly") {
      yearlySavings = rawMonthly * (discountPercent / 100);
      yearlyNet = rawMonthly - yearlySavings;
      monthlyEquiv = yearlyNet / 12;
    }
  }

  return (
    <div className="container myn-signup">
      <h1 className="myn-signup__title">QR-Code selbst buchen</h1>
      <p className="myn-signup__lead">
        Angebot wählen, E-Mail-Adresse und Passwort festlegen. Ihr Vertragspartner ist{" "}
        {catalog.host ? catalog.host.company_name : "der Betreiber"}.
        Firmierung und Anschrift ergänzen Sie nach der Anmeldung.
      </p>

      {trial && trial.company_name && (
        <Alert variant="info">
          Getesteter QR-Code für <strong>{trial.company_name}</strong>
          {trial.company_address ? ` (${trial.company_address})` : ""}. Er wird diesem Konto zugeordnet,
          wenn Sie dasselbe Angebot gebucht lassen.
        </Alert>
      )}

      <h2 className="myn-signup__section">1. Angebot wählen</h2>
      <div className="myn-signup__catalog">
        {catalog.offers.map((offer) => {
          const isSelected = String(offer.batch_id) === String(selectedBatchId);
          const period = PERIOD_LABELS[offer.tariff.amount_period_net] || "";
          const fee = Number(offer.tariff.activation_fee_eur_net) || 0;
          const soldOut = Number(offer.available) <= 0;
          return (
            <button
              type="button"
              key={offer.batch_id}
              onClick={() => setSelectedBatchId(offer.batch_id)}
              aria-pressed={isSelected}
              disabled={soldOut}
              className={`myn-signup__offer${isSelected ? " myn-signup__offer--selected" : ""}`}
            >
              <span className="myn-signup__offer-service">{offer.service.service_name}</span>
              <span className="myn-signup__offer-price">
                {formatAmount(offer.tariff.amount_eur_net)} € netto {period}
              </span>
              {fee > 0 && (
                <span className="myn-signup__offer-fee">
                  zzgl. {formatAmount(fee)} € einmalige Aktivierungsgebühr
                </span>
              )}
              {offer.service.description && (
                <span className="myn-signup__offer-text">{offer.service.description}</span>
              )}
              <span className="myn-signup__offer-meta">
                {offer.batch_name} · Tarif: {offer.tariff.tariff_name}
                {soldOut ? " · derzeit vergriffen" : ` · noch ${offer.available} verfügbar`}
              </span>
            </button>
          );
        })}
      </div>
      {errors.batch_id && <div className="text-danger small mb-3">{errors.batch_id}</div>}

      {/* Zahlungsweise auswählen */}
      {selectedOffer && (
        <div className="mb-4 p-3 bg-light rounded border">
          <Form.Label className="fw-bold mb-2">Zahlungsweise wählen</Form.Label>
          <div className="d-flex flex-column flex-sm-row gap-3">
            <Form.Check
              type="radio"
              id="billingYearly"
              name="billingPeriodChoice"
              checked={billingPeriod === "yearly"}
              onChange={() => setBillingPeriod("yearly")}
              label={
                <span>
                  <strong>Jährliche Vorauszahlung</strong>{" "}
                  <span className="badge bg-success ms-1">20% Rabatt</span>
                  <br />
                  <span className="text-muted small">
                    {formatAmount(yearlyNet)} € netto / Jahr (= {formatAmount(monthlyEquiv)} €/Monat)
                    {yearlySavings > 0 && <span className="text-success ms-1">· Sie sparen {formatAmount(yearlySavings)} €!</span>}
                  </span>
                </span>
              }
            />
            <Form.Check
              type="radio"
              id="billingMonthly"
              name="billingPeriodChoice"
              checked={billingPeriod === "monthly"}
              onChange={() => setBillingPeriod("monthly")}
              label={
                <span>
                  <strong>Monatliche Zahlung</strong>
                  <br />
                  <span className="text-muted small">
                    {formatAmount(rawMonthly)} € netto / Monat
                  </span>
                </span>
              }
            />
          </div>
        </div>
      )}

      <h2 className="myn-signup__section">2. E-Mail und Passwort</h2>
      {apiError && <Alert variant="danger">{apiError}</Alert>}

      <Form onSubmit={handleSubmit} noValidate>
        <div className="myn-signup__grid">
          <Form.Group controlId="ssEmail">
            <Form.Label>E-Mail-Adresse *</Form.Label>
            <Form.Control
              type="email"
              name="custumer_company_email"
              value={form.custumer_company_email}
              onChange={handleChange}
              isInvalid={!!errors.custumer_company_email}
              autoComplete="email"
            />
            <Form.Text className="text-muted">Dies ist zugleich Ihr Benutzername. Die Adresse muss bestätigt werden.</Form.Text>
            <Form.Control.Feedback type="invalid">{errors.custumer_company_email}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="ssPassword">
            <Form.Label>Passwort *</Form.Label>
            <Form.Control
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              isInvalid={!!errors.password}
              autoComplete="new-password"
            />
            <Form.Text className="text-muted">
              Mindestens {MIN_PASSWORD_LENGTH} Zeichen, Buchstaben und Ziffern.
            </Form.Text>
            <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="ssPasswordRepeat">
            <Form.Label>Passwort wiederholen *</Form.Label>
            <Form.Control
              type="password"
              name="password_repeat"
              value={form.password_repeat}
              onChange={handleChange}
              isInvalid={!!errors.password_repeat}
              autoComplete="new-password"
            />
            <Form.Control.Feedback type="invalid">{errors.password_repeat}</Form.Control.Feedback>
          </Form.Group>
        </div>

        <Form.Group controlId="ssAgb" className="myn-signup__consent">
          <Form.Check
            type="checkbox"
            name="agb_consent"
            checked={form.agb_consent}
            onChange={handleChange}
            isInvalid={!!errors.agb_consent}
            label={
              <>
                Ich habe die <Link to="/agb">Allgemeinen Geschäftsbedingungen</Link> gelesen und akzeptiere sie. *
              </>
            }
          />
          {errors.agb_consent && <div className="text-danger small mt-1">{errors.agb_consent}</div>}
        </Form.Group>

        <Form.Group controlId="ssTariff" className="myn-signup__consent">
          <Form.Check
            type="checkbox"
            name="tariff_consent"
            checked={form.tariff_consent}
            onChange={handleChange}
            isInvalid={!!errors.tariff_consent}
            label={
              selectedOffer ? (
                billingPeriod === "yearly" ? (
                  <>
                    Ich habe die Preis- und Tarifkonditionen gelesen und akzeptiere sie:
                    {" "}{formatAmount(yearlyNet)} € netto pro Jahr (inkl. {discountPercent}% Rabatt, entspricht {formatAmount(monthlyEquiv)} €/Monat)
                    {activationFee > 0 ? `, zuzüglich ${formatAmount(activationFee)} € einmalige Aktivierungsgebühr` : ""}
                    , zuzüglich gesetzlicher Steuer. Tarif: {selectedOffer.tariff.tariff_name}. *
                  </>
                ) : (
                  <>
                    Ich habe die Preis- und Tarifkonditionen gelesen und akzeptiere sie:
                    {" "}{formatAmount(rawMonthly)} € netto pro Monat
                    {activationFee > 0 ? `, zuzüglich ${formatAmount(activationFee)} € einmalige Aktivierungsgebühr` : ""}
                    , zuzüglich gesetzlicher Steuer. Tarif: {selectedOffer.tariff.tariff_name}. *
                  </>
                )
              ) : (
                <>Ich habe die Preis- und Tarifkonditionen des gewählten Angebots gelesen und akzeptiere sie. *</>
              )
            }
          />
          {errors.tariff_consent && <div className="text-danger small mt-1">{errors.tariff_consent}</div>}
        </Form.Group>

        <Form.Group controlId="ssPrivacy" className="myn-signup__consent">
          <Form.Check
            type="checkbox"
            name="privacy_consent"
            checked={form.privacy_consent}
            onChange={handleChange}
            isInvalid={!!errors.privacy_consent}
            label={
              <>
                Ich habe die <Link to="/datenschutz">Datenschutzerklärung</Link> gelesen und
                stimme der Verarbeitung meiner Daten zur Vertragsabwicklung zu. *
              </>
            }
          />
          {errors.privacy_consent && (
            <div className="text-danger small mt-1">{errors.privacy_consent}</div>
          )}
        </Form.Group>

        <Button type="submit" disabled={submitting} className="myn-signup__submit">
          {securityChecking
            ? "Sicherheitsprüfung läuft …"
            : submitting
            ? "Wird gebucht …"
            : "Kostenpflichtig buchen"}
        </Button>
        {FEATURES.altcha && (
          <Form.Text className="text-muted d-block mt-2">
            Geschützt durch eine automatische Sicherheitsprüfung (ALTCHA,
            Proof-of-Work). Es werden keine persönlichen Daten an Dritte
            übertragen und keine Cookies gesetzt.
          </Form.Text>
        )}
        <p className="myn-signup__hint">
          Nach der E-Mail-Bestätigung melden Sie sich an. Den QR-Code finden Sie unter „Meine QR-Codes“.
        </p>
      </Form>
    </div>
  );
};

export default SelfServiceSignup;
